/**
 * Which panel navigations earn a browser history entry. One a person causes — a click, a key, a
 * drop, a request to the AI chat — earns one, however many URL writes it takes. What the app does
 * on its own (a load filling in a default, a timer, the aftermath of Back itself) rewrites the
 * entry it is on: an entry the app moves away from by itself is a Back that seems to do nothing.
 */

/** One person's action, from its input to the end of the task it arrived in. */
interface NavigationGesture {
    /** Whether a navigation of this gesture has already added its history entry. */
    isPushed: boolean;
}

// The inputs a person navigates with. Moves, scrolls and hovers navigate nothing, and `popstate`
// is a step through history rather than a new entry in it.
const GESTURE_EVENTS = [
    'pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'dblclick', 'auxclick',
    'contextmenu', 'keydown', 'keyup', 'touchend', 'submit', 'change', 'drop',
];

class NavigationGestures {
    private gesture: NavigationGesture | null = null;

    /** The gesture a navigation made now belongs to, or null when the app is acting alone. */
    getCurrent(): NavigationGesture | null {
        return this.gesture;
    }

    /** Run `act` as a person's action that arrived without an input: the AI chat's tools. */
    runAs<T>(act: () => T): T {
        if (this.gesture) {
            return act();
        }
        this.gesture = { isPushed: false };
        try {
            return act();
        } finally {
            this.gesture = null;
        }
    }

    /** Follow the window's inputs; returns the cleanup. */
    track(target: Window): () => void {
        for (const type of GESTURE_EVENTS) {
            target.addEventListener(type, this.begin, { capture: true });
        }
        target.addEventListener('popstate', this.end, { capture: true });
        return (): void => {
            for (const type of GESTURE_EVENTS) {
                target.removeEventListener(type, this.begin, { capture: true });
            }
            target.removeEventListener('popstate', this.end, { capture: true });
            this.gesture = null;
        };
    }

    // The renders and effects an input flushes run in its task, so the gesture lasts to the end
    // of that task.
    private readonly begin = (event: Event): void => {
        // A held key repeats into the entry its first press made.
        if (this.gesture || (event instanceof KeyboardEvent && event.repeat)) {
            return;
        }
        const gesture = { isPushed: false };
        this.gesture = gesture;
        setTimeout((): void => {
            if (this.gesture === gesture) {
                this.gesture = null;
            }
        }, 0);
    };

    // Keyboard Back arrives as a keydown first, and what Back restores is no new entry.
    private readonly end = (): void => {
        this.gesture = null;
    };
}

export const navigationGestures = new NavigationGestures();
